<template>
  <div
    class="root"
    :class="rootClass"
    @keydown.capture="handleRootKeydown"
    @pointerdown="handleRootPointerdown"
    @click.capture="handleRootClick"
  >
    <sp-label v-if="label" for="select" block>{{ label }}</sp-label>
    <sp-quantum-select
      id="select"
      ref="container"
      v-preserve-properties="propertyBindings(remoteBindings, quantumSelectProps)"
      v-preserve-properties:map-results="mapResults"
      v-preserve-properties:max-results="props.maxResults"
      v-preserve-properties:items="listItems"
      v-preserve-properties:selected="pickerSelected"
      v-bind="attributeBindings(remoteBindings, quantumSelectProps)"
      :no-filter="props.noFilter"
      :show-search-bar-in-sheet="remoteEnabled"
      class="sp-select"
      :navigation="props.navigation"
      :search-all-levels="searchAllLevelsAttr"
      :selectable-branches="selectableBranchesAttr"
      :branch-toggle-label="props.branchToggleLabel"
      :hide-search-bar="hideSearchBarAttr"
      :placeholder="props.searchPlaceholder"
      :disabled="disabledAttr"
      :fuzzy-search="fuzzySearchAttr"
      :search="pickerSearch"
      :close-on-select="closeOnSelectAttr"
      :class="[modifierClass, selectedTagsAppearance]"
      :open="menuShown"
      :item-title="pickerItemTitle"
      :item-value="pickerItemValue"
      :multiple="multiple"
      :selected-tags="props.selectedTags"
      :selection-clearable="props.selectionClearable"
      :required="props.required"
      :appearance="menuAppearance"
      :hide-no-data="hideNoData"
      :aria-label="listLabel"
      :item-test-id="props.itemTestId"
      :highlight-first-result="highlightFirstResult"
      :highlight-idle-first-result="highlightIdleFirstResult"
      :field-commits-highlight="fieldCommitsHint"
      :wrap="props.wrap"
      :pointer="props.pointer"
      :no-sheet="props.noSheet"
      :no-restore-focus="leavesFocusOnClose"
      :max-height="SELECT_MENU_MAX_HEIGHT"
      :selected-prepend-icon="props.selectedIcon"
      :selected-icon-position="props.selectedIconPosition"
      @remote-state="handleRemoteState"
      @remote-load="handleRemoteLoad"
      @update-open="handleMenuUpdateOpen"
      @search-enter="handleSearchEnter"
      @update-search="handleSearchUpdate"
      @update-matches="handleMatchesUpdate"
      @update-selected="handleUpdateSelected"
      @keydown.down="handleMenuKeyDown"
      @keydown.up="handleMenuKeyDown"
    >
      <!-- The forwarded slot shadows the panel's own fallback, so the text is repeated here. -->
      <slot v-if="forwardsNoData" slot="no-data" name="no-data">{{ noDataMessage }}</slot>
      <!-- On the wrapper, so a slotted activator gets the same close paths as the default field. -->
      <div
        slot="activator"
        class="sp-select-activator"
        @click="handleActivatorClick"
        @keydown.escape="closeMenu"
        @focusout="handleActivatorFocusOut"
      >
        <slot ref="defaultActivatorSlot" name="activator">
          <!-- `.attr` seeds the field; selection changes write the live `value` property below. -->
          <sp-text-field
            ref="activatorField"
            class="sp-select-activator__input"
            :class="selectedTagsAppearance"
            :inputmode="inputmode"
            :readonly="inputmode === 'none'"
            :value.attr="activatorText"
            :title="activatorTooltip"
            :aria-label="activatorAriaLabel"
            :disabled="disabledAttr"
            :placeholder="activatorPlaceholder"
            :loading="loading"
            :density="activatorDensity"
            :variant="selectedTagsVariant"
            propagate-append-click
            @click.prevent.stop="toggleMenuIntent"
            @keydown.enter="handleActivatorEnter"
            @keydown.tab="handleActivatorTab"
            @input="handleTextInput"
          >
            <div slot="prepend">
              <sp-icon
                v-if="activatorIcon"
                class="sp-select-activator__icon"
                :name="activatorIcon"
                aria-hidden="true"
              ></sp-icon>
              <slot name="prepend" @click="handlePrependClick"></slot>
            </div>
            <div v-if="!selectionFieldEnabled" ref="appendArea" slot="append" @click="handleAppendClick">
              <slot ref="appendSlot" name="append"></slot>
              <template v-if="showsCaret">
                <span v-if="props.caretVariant === 'image'" class="sp-select__caret-image"></span>
                <sp-caret v-else :active="caretActive" :variant="props.caretVariant"></sp-caret>
              </template>
            </div>
          </sp-text-field>
        </slot>
      </div>
      <div v-if="showSelectionTrailing" :slot="selectionTrailingSlot" @click="handleActivatorClick">
        <div ref="appendArea" class="sp-select__trailing-append" @click="handleAppendClick">
          <slot ref="appendSlot" name="append"></slot>
          <template v-if="showsCaret">
            <span v-if="props.caretVariant === 'image'" class="sp-select__caret-image"></span>
            <sp-caret v-else :active="caretActive" :variant="props.caretVariant"></sp-caret>
          </template>
        </div>
      </div>
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { attributeBindings, propertyBindings, vPreserveProperties } from "#ce/utils/property-bindings";
import { onClickOutside } from "@vueuse/core";
import { computed, ref, toRef, useAttrs, useTemplateRef, watchEffect } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { useHostElement } from "#ce/composables/useHostElement";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { useActivatorFocus } from "./composables/useActivatorFocus";
import { useSelectedTagsAppearance } from "./composables/useSelectedTagsAppearance";
import { useActivatorText } from "./composables/useActivatorText";
import { useAppendCaret } from "./composables/useAppendCaret";
import { useCustomItems } from "./composables/useCustomItems";
import { useDefaultSelection } from "./composables/useDefaultSelection";
import { useFirstResultCommit } from "./composables/useFirstResultCommit";
import { useItemIcons } from "./composables/useItemIcons";
import { useMenuIntent } from "./composables/useMenuIntent";
import { useNoDataForwarding } from "./composables/useNoDataForwarding";
import { useOfferedItems } from "./composables/useOfferedItems";
import { useOpenListKeys } from "./composables/useOpenListKeys";
import { useSelectActivatorBlur } from "./composables/useSelectActivatorBlur";
import { useSelectActivatorInput } from "./composables/useSelectActivatorInput";
import { useSelectAppearance } from "./composables/useSelectAppearance";
import { useSelectCommit } from "./composables/useSelectCommit";
import { useSelectionValue } from "./composables/useSelectionValue";
import { useSelectLabels } from "./composables/useSelectLabels";
import { useSelectSearch } from "./composables/useSelectSearch";
import { isRemoteSearch, props as quantumSelectProps, remoteProps } from "#ce/components/SpQuantumSelect/props";
import { RemoteStatus } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectRemote";
import { useQuantumSelectSelectionRegistry } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectSelectionRegistry";
import { useSelectSubmission } from "./composables/useSelectSubmission";
import { toArray, toBoolean } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";
import { whenI18nReady } from "./i18n";
import { props as selectProps, selectPropertyDefinitions } from "./props.js";

const SELECT_MENU_MAX_HEIGHT = "var(--sp-comp-select-list-max-height, 325px)";

// sp-select is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
defineOptions(
  formAssociatedHost({
    inheritAttrs: false,
    propertyDefinitions: selectPropertyDefinitions,
    shadowRootOptions: {
      delegatesFocus: true,
    },
  }),
);

const props = defineProps(selectProps);
const emit = defineEmits([
  "input",
  "text-input",
  "click-append",
  "click-prepend",
  "update-menu",
  "update-search",
  "remote-state",
  "remote-load",
]);

const { emitBubbling } = useHostEmit();
const hostElement = useHostElement();

const remoteEnabled = computed(() => isRemoteSearch(props));
const forwardsNoData = useNoDataForwarding(props, {
  remoteEnabled,
  hostElement,
  defaultText: selectProps.noDataText.default,
});
const remoteBindings = computed(() => {
  const bindings = Object.fromEntries(Object.keys(remoteProps).map((key) => [key, props[key]]));

  if (remoteEnabled.value && toBoolean(props.allowCustom)) {
    bindings.remoteLoader = invalidRemoteLoader;
  }

  return bindings;
});

function invalidRemoteLoader() {
  throw Object.assign(new Error("Remote creation is unsupported"), { kind: "configuration" });
}

const remoteItems = ref([]);
const remoteState = ref({ status: RemoteStatus.IDLE, query: "", page: 0, hasMore: false, error: null });
let registry;

const container = useTemplateRef("container");
const activatorField = useTemplateRef("activatorField");
const appendArea = useTemplateRef("appendArea");
const appendSlot = useTemplateRef("appendSlot");

const i18nReady = ref(false);
whenI18nReady.then(markI18nReady);

function markI18nReady() {
  i18nReady.value = true;
}

const loading = computed(() => toBoolean(props.loading));
const returnObject = computed(() => toBoolean(props.returnObject));
const multiple = computed(() => toBoolean(props.multiple));
// Same null-not-false pattern for the fuzzy in-list matching opt-in.
const fuzzySearchAttr = computed(() => toBoolean(props.fuzzySearch) || null);
const searchAllLevelsAttr = computed(() => toBoolean(props.searchAllLevels) || null);
const selectableBranchesAttr = computed(() => toBoolean(props.selectableBranches) || null);
const keepOpenOnSelect = computed(() => toBoolean(props.keepOpenOnSelect));
// Same null-not-false attribute pattern: drop `close-on-select` from the inner
// quantum-select when the menu should stay open across selections.
const closeOnSelectAttr = computed(() => !keepOpenOnSelect.value || null);
// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({
  disabled: toRef(props, "disabled"),
});
const { getItemTitle, getItemValue, findItem: findListItem } = useSpList(props);
// Bind null (not false) so the activator field never renders a stray `disabled="false"`,
// alongside the registration-level normalization.
const disabledAttr = computed(() => disabled.value || null);

const attrs = useAttrs();
const { exposeProperties, exposeMethods } = useExpose();

const { items, hasItems, addCustomItem } = useOfferedItems(props, {
  attrs,
  exposeProperties,
});

const {
  pickerItemTitle,
  pickerItemValue,
  getPickerItemValue,
  searchable,
  hideSearchBarAttr,
  highlightIdleFirstResult,
  pickerSearch,
  canOpenList,
  highlightFirstResult,
  pickerSelected,
  mapResults,
  noDataMessage,
  updateMatches,
  updateActivatorSearch,
  pickerItems,
  hasCustomOffer,
  updateSearch,
  clearSearch,
  commitCustomItem,
} = useCustomItems(props, {
  i18nReady,
  items,
  hasItems,
  selected: () => model.value,
  getItemTitle,
  getItemValue,
  findItem,
  addCustomItem,
});

const {
  menuOpen,
  menuShown,
  closeMenu,
  toggleMenuIntent,
  handleMenuUpdateOpen,
  handleMenuKeyDown,
  handleActivatorClick,
} = useMenuIntent(props, {
  disabled,
  hasItems: canOpenList,
  container,
  appendArea,
  emit,
});

const {
  leavesFocusOnClose,
  handleKeydown: handleRootKeydown,
  handlePointerdown: handleRootPointerdown,
  handleClick: handleRootClick,
} = useOpenListKeys(props, {
  shown: menuShown,
  hostElement,
});

const { rootClass, modifierClass, menuAppearance, activatorVariant, activatorDensity } = useSelectAppearance(props, {
  disabled,
  menuShown,
});

const { showsCaret, caretActive, hasTrailingContent } = useAppendCaret(props, {
  appendSlot,
  hasItems,
  menuShown,
});

const defaultActivatorSlot = useTemplateRef("defaultActivatorSlot");
const {
  selectedTagsAppearance,
  selectedTagsEnabled,
  selectionFieldEnabled,
  selectedTagsVariant,
  showSelectionTrailing,
  selectionTrailingSlot,
} = useSelectedTagsAppearance(props, {
  activatorSlot: defaultActivatorSlot,
  hasTrailingContent,
  multiple,
  variant: activatorVariant,
  density: activatorDensity,
});

const { model, defaultValue, initialModel, promptNormalized, setSelection, resetSelection } = useDefaultSelection(
  props,
  {
    attrs,
    multiple,
    returnObject,
    findItem,
    getItemValue,
  },
);

exposeProperties({
  remoteState: { configurable: true, get: () => container.value?.remoteState ?? remoteState.value },
  remoteItems: { configurable: true, get: () => pickerRemoteItems() ?? remoteItems.value },
  resolvedSelectedItems: { configurable: true, get: () => registry?.rows.value ?? [] },
  type: {
    configurable: true,
    get: () => (multiple.value ? "select-multiple" : "select-one"),
  },
  value: {
    configurable: true,
    get: () => selection.value,
    set: setSelection,
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

function currentDefaults() {
  return initialModel();
}

registry = useQuantumSelectSelectionRegistry(
  {
    get items() {
      return items.value;
    },
    get selectedItems() {
      return props.selectedItems;
    },
  },
  {
    selection: model,
    defaults: computed(currentDefaults),
    offered: remoteItems,
    getItemValue,
  },
);
const form = useForm();

const { resolvedItem, hasSelection } = useSelectSubmission(props, {
  model,
  items,
  defaultValue,
  multiple,
  initialModel,
  findItem,
  getItemValue,
  form,
  anchor: container,
  remoteOrder: computed(() => (remoteEnabled.value ? registry.order.value : null)),
});

const { selection } = useSelectionValue({
  model,
  multiple,
  returnObject,
  resolvedItem,
});

form.onFormReset(restoreDefaultSelection);

// `resolvedItem` remembers the row it resolves, so the icon does not blink away while the list is
// replaced under an existing selection.
const selectedItem = computed(() => resolvedItem(model.value[0]));

const {
  listItems,
  selectedIcon: activatorIcon,
  iconOnly,
} = useItemIcons(props, {
  items: pickerItems,
  selectedItem,
  multiple,
});

const { activatorAriaLabel, listLabel, activatorTooltip, activatorPlaceholder } = useSelectLabels(props, {
  hostElement,
  field: activatorField,
  hasSelection,
  iconOnly,
  selectedTagsEnabled,
  selectedItem,
  getItemTitle,
});

const { activatorText, restoreActivatorText } = useActivatorText(props, {
  selectedTagsEnabled,
  model,
  items,
  multiple,
  iconOnly,
  hasSelection,
  menuOpen,
  field: activatorField,
  findItem,
  getItemTitle,
});

watchEffect(syncIconOnlyMarker);

const { handleSearchUpdate, handleMatchesUpdate } = useSelectSearch({
  menuOpen,
  updateSearch,
  updateMatches,
  clearSearch,
  emit,
});

const { fieldCommitsHint, selectFirstResult, handleActivatorEnter, handleActivatorTab, handleSearchEnter } =
  useFirstResultCommit(props, {
    disabled,
    searchable,
    hasCustomOffer,
    keepOpenOnSelect,
    menuOpen,
    menuShown,
    container,
    findItem,
    getItemValue: getPickerItemValue,
    closeMenu,
  });

const { handleUpdateSelected } = useSelectCommit({
  model,
  selection,
  keepOpenOnSelect,
  promptNormalized,
  commitCustomItem,
  closeMenu,
  emitBubbling,
  form,
});

const { handleTextInput } = useSelectActivatorInput({
  menuOpen,
  updateActivatorSearch,
  remoteEnabled,
  emit,
});

const { handleActivatorFocusOut } = useSelectActivatorBlur({
  container,
  menuOpen,
  menuShown,
  restoreActivatorText,
});

const { focus } = useActivatorFocus({
  hostElement,
  field: activatorField,
});

exposeMethods({
  loadMore,
  retryRemote,
  reloadRemote,
  selectFirstResult,
  focus,
});

onClickOutside(container, closeMenu);

function findItem(value) {
  if (remoteEnabled.value) {
    return (
      registry?.resolve(value) ?? findListItem(value, toArray(props.selectedItems)) ?? findListItem(value, items.value)
    );
  }

  return findListItem(value, items.value);
}

function handleRemoteState({ detail }) {
  remoteItems.value = pickerRemoteItems() ?? [];
  remoteState.value = detail[0];
  emit("remote-state", ...detail);
}

function pickerRemoteItems() {
  return container.value?.remoteItems;
}

function handleRemoteLoad({ detail }) {
  remoteItems.value = detail[0].items;
  emit("remote-load", ...detail);
}

function loadMore() {
  return container.value?.loadMore();
}

function retryRemote() {
  return container.value?.retryRemote();
}

function reloadRemote() {
  return container.value?.reloadRemote();
}

function restoreDefaultSelection() {
  clearSearch();
  resetSelection();
  // A reset that lands on the value the model already holds moves no watcher, so the activator
  // keeps whatever the user typed unless it is written here.
  restoreActivatorText();
}

function syncIconOnlyMarker() {
  hostElement?.toggleAttribute("data-icon-only", iconOnly.value);
}

function handlePrependClick() {
  emit("click-prepend");
}

function handleAppendClick() {
  emit("click-append");
}
</script>

<style>
:host {
  display: block;
  height: fit-content;
  --sp-comp-divider-color: var(--sp-comp-select-divider-color, #ccc);
  --sp-comp-quantum-select-popup-min-height: 0;
  --sp-comp-popup-min-height: 0;
  /* Shield from an ancestor's --sp-comp-popup-width and --sp-comp-menu-width / -min-width so the
     popup matches the activator. */
  --sp-comp-popup-width: var(--sp-comp-select-popup-width, 100%);
  --sp-comp-menu-width: initial;
  --sp-comp-menu-min-width: initial;
}

/* An icon is the whole control here, so it takes an icon's width rather than its container's. The
   popup still opens at its own width, which --appearance-text sets to max-content. Keyed off the
   resolved mode, since `icon-only` with no icon to show still renders its title. */
:host([data-icon-only]) {
  display: inline-block;
  --sp-comp-popup-width: max-content;
}
</style>

<style lang="scss" scoped>
@use "sass:map";
@import "@ce/components/SpTextField/styles/defaults";
@import "@assets/mixins/breakpoint";

/* Each `--sp-comp-select-*` field and list token sets the inner element tokens it names. An unset
   one leaves the value a page sets on the host for that inner token, then the inner default. */
$field-tokens: (
  "field-min-width": "text-field-min-width",
  "field-min-height": "text-field-min-height",
  "field-compact-min-height": "text-field-compact-min-height",
  "field-padding-inline": "text-field-padding-inline",
  "field-compact-padding-inline": "text-field-compact-padding-inline",
  "field-padding-block": "text-field-padding-block",
  "field-compact-padding-block": "text-field-compact-padding-block",
  "field-background-color": "text-field-background-color",
  "field-border-color": "text-field-border-color",
  "field-border-width": "text-field-border-width",
  "field-border-style": "text-field-border-style",
  "field-border-radius": "text-field-border-radius",
  "field-mobile-border-radius": "text-field-mobile-border-radius",
  "field-outline-offset": "text-field-outline-offset",
  "field-color": "text-field-color",
  "field-font-family": "text-field-font",
  "field-font-size": "text-field-font-size",
  "field-line-height": "text-field-line-height",
  "field-focus-border-color": "text-field-focus-border-color",
  "field-focus-box-shadow": "text-field-focus-box-shadow",
  "field-focus-outline": "text-field-focus-outline",
  "field-focus-outline-offset": "text-field-focus-outline-offset",
  "field-disabled-opacity": "text-field-disabled-opacity",
  "caret-offset": "caret-offset",
);
$list-tokens: (
  "list-offset": "quantum-select-popup-offset",
  "list-offset-inline": "quantum-select-popup-offset-inline",
  "list-pointer-size": "quantum-select-popup-pointer-size",
  "list-pointer-offset": "quantum-select-popup-pointer-offset",
  "list-background-color": "quantum-select-popup-background-color",
  "list-box-shadow": "quantum-select-popup-box-shadow",
  "list-border-radius": "quantum-select-popup-border-radius",
  "list-padding-block-start": "quantum-select-content-padding-top",
  "list-padding-block-end": "quantum-select-content-padding-bottom",
  "item-min-height": "quantum-select-item-min-height",
  "item-padding-block": "quantum-select-item-padding-block",
  "item-padding-inline": "quantum-select-item-padding-inline",
  "item-align-items": "quantum-select-item-align-items",
  "item-color": "quantum-select-item-color",
  "item-font": "quantum-select-item-font",
  "item-line-height": "quantum-select-item-line-height",
  "item-hover-background-color": "quantum-select-item-hover-background-color",
  "item-hover-color": "quantum-select-item-hover-color",
  "item-focused-background-color": "quantum-select-item-focused-background-color",
  "item-focus-box-shadow": "quantum-select-item-focus-box-shadow",
  "item-selected-background-color": "quantum-select-item-selected-background-color",
  "item-selected-hover-background-color": "quantum-select-item-selected-hover-background-color",
  "item-selected-color": "quantum-select-item-selected-color",
  "item-selected-font": "quantum-select-item-selected-font",
  "selected-icon-color": "quantum-select-selected-prepend-icon-fill-color",
  "selected-icon-offset": "quantum-select-item-icon-offset",
  "selected-icon-width": "quantum-select-item-icon-width",
  "selected-icon-gap": "quantum-select-item-icon-gap",
  "selected-icon-size": "quantum-select-item-selected-icon-size",
);

.root {
  --sp-comp-popup-min-width: 100% !important;
  /* The popup is the activator's width, so the picker's minimum panel width is off and the panel
     fits the popup. */
  --sp-comp-quantum-select-popup-width: 0;
  --sp-comp-list-item-content-white-space: var(--sp-comp-select-item-white-space, nowrap);
  /* A title wider than the popup scrolls the panel sideways. The list spans that scroll width, so a
     scrolled row stays full width. */
  --sp-comp-list-min-width: var(--sp-comp-select-list-min-width, max-content);
  --caret-size: var(--sp-comp-select-caret-size, var(--sp-comp-caret-size, var(--sp-sys-icon-size-medium)));

  @each $token, $inners in map.merge($field-tokens, $list-tokens) {
    @each $inner in $inners {
      --select-host-#{$inner}: var(--sp-comp-#{$inner});
    }
  }

  &.--density-compact {
    --caret-size: var(
      --sp-comp-select-compact-caret-size,
      var(--sp-comp-select-caret-size, var(--sp-comp-caret-size, var(--sp-sys-icon-size-medium)))
    );
  }

  &.--wrap {
    --sp-comp-list-item-content-white-space: normal;
    --sp-comp-list-min-width: auto;
  }

  &.--is-active {
    --select-active-border-color: var(
      --sp-comp-select-active-border-color,
      var(--sp-sys-form-field-focus-border-color, var(--sp-sys-color-primary))
    );
  }

  &.--appearance-text {
    /* `em` throughout, so the control takes the surrounding text's size. */
    --sp-comp-text-field-font-size: 1em;
    --sp-comp-text-field-compact-min-height: var(--sp-comp-select-text-min-height, 1.5rem);
    --caret-size: var(--sp-comp-select-caret-size, var(--sp-comp-caret-size, 1.25em));
    --sp-comp-caret-offset: 0;

    /* The field hugs the selected label instead of filling its container. */
    --sp-comp-text-field-min-width: 0;
    --sp-comp-text-field-width: auto;
    --sp-comp-text-field-field-sizing: content;

    /* The dropdown grows to its widest entry, which the hugging activator can be narrower than. */
    --sp-comp-popup-width: max-content;

    /* Only the activator takes the surrounding text's size. The rows state a size of their own,
       because `sp-list-item` sizes its title by inheritance wherever the typescale tokens are
       undefined — as they are in admin — which would otherwise shrink the menu with the activator. */
    --sp-comp-list-item-title-font-size: var(--sp-sys-typescale-body-font-size, 1rem);
  }
}

/* The field while the list is open: the open border holds over the field tokens, focused or not. */
.root.--is-active .sp-select-activator__input:not(.--selection-field) {
  --sp-comp-text-field-border-color: var(--select-active-border-color);
  --sp-comp-text-field-focus-border-color: var(--select-active-border-color);
  --sp-comp-text-field-focus-outline: var(
    --sp-comp-select-active-outline,
    var(--sp-comp-select-field-focus-outline, var(--select-host-text-field-focus-outline))
  );
  --sp-comp-text-field-focus-box-shadow: var(
    --sp-comp-select-active-box-shadow,
    var(--sp-comp-select-field-focus-box-shadow, var(--select-host-text-field-focus-box-shadow))
  );
}

.sp-select-activator__input {
  @each $token, $inner in $field-tokens {
    --sp-comp-#{$inner}: var(--sp-comp-select-#{$token}, var(--select-host-#{$inner}));
  }

  box-shadow: var(--sp-comp-select-field-box-shadow, none);
  border-radius: var(
    --sp-comp-select-field-border-radius,
    var(--select-host-text-field-border-radius, var(--sp-sys-shape-control))
  );

  /* The field takes its mobile corners on a phone, so the shadow does too. */
  @include sp-breakpoint(xs) {
    border-radius: var(
      --sp-comp-text-field-mobile-border-radius,
      var(
        --sp-comp-select-field-border-radius,
        var(--select-host-text-field-border-radius, var(--sp-sys-shape-control))
      )
    );
  }
}

.sp-select {
  --sp-comp-quantum-select-selected-tags-font-family: var(
    --sp-comp-select-field-font-family,
    var(--select-host-text-field-font, var(--sp-sys-form-field-font-family))
  );
  --sp-comp-quantum-select-selected-tags-font-size: var(
    --sp-comp-select-field-font-size,
    var(--select-host-text-field-font-size, var(--sp-sys-form-field-font-size))
  );
  --sp-comp-quantum-select-selected-tags-font-weight: var(--sp-sys-form-field-font-weight);
  --sp-comp-quantum-select-selected-tags-line-height: var(
    --sp-comp-select-field-line-height,
    var(--select-host-text-field-line-height, normal)
  );
  --sp-comp-quantum-select-selected-tags-letter-spacing: normal;

  @each $token, $inners in $list-tokens {
    @each $inner in $inners {
      --sp-comp-#{$inner}: var(--sp-comp-select-#{$token}, var(--select-host-#{$inner}));
    }
  }
}

.sp-select.--selection-field {
  --select-tags-default-color: currentColor;
  --sp-comp-quantum-select-selected-tags-color: var(
    --sp-comp-select-field-color,
    var(--select-host-text-field-color, var(--sp-sys-form-field-color, var(--select-tags-default-color)))
  );
  --sp-comp-quantum-select-selected-tags-content-padding-block: var(
    --sp-comp-select-selected-tags-content-padding-block,
    var(--sp-ref-scale-4)
  );
  --sp-comp-quantum-select-selected-tags-input-basis: var(
    --sp-comp-select-selected-tags-input-basis,
    var(--sp-ref-scale-64)
  );
  @each $token, $default in text-field-defaults() {
    --select-tags-default-#{$token}: var(
      --sp-comp-select-field-#{$token},
      var(--select-host-text-field-#{$token}, #{$default})
    );
    --sp-comp-quantum-select-selected-tags-#{$token}: var(--select-tags-default-#{$token});
  }
  --sp-comp-quantum-select-selected-tags-box-shadow: var(--sp-comp-select-field-box-shadow, none);
  --sp-comp-quantum-select-selected-tags-clear-button-height: min(
    var(
      --sp-ref-scale-32,
      calc(
        var(--sp-comp-quantum-select-selected-tags-min-height) - 2 *
          var(--sp-comp-quantum-select-selected-tags-padding-block) - 2 *
          var(--sp-comp-quantum-select-selected-tags-border-width)
      )
    ),
    var(--sp-comp-quantum-select-selected-tags-min-height)
  );

  &.--tags-density-compact {
    --sp-comp-quantum-select-selected-tags-chip-padding-block: 0;
    --sp-comp-quantum-select-selected-tags-content-padding-block: var(
      --sp-comp-select-selected-tags-content-padding-block,
      0
    );

    @each $token in "min-height", "padding-inline", "padding-block" {
      --sp-comp-quantum-select-selected-tags-#{$token}: var(
        --sp-comp-select-field-compact-#{$token},
        var(
          --select-host-text-field-compact-#{$token},
          var(--sp-sys-form-field-compact-#{$token}, var(--select-tags-default-#{$token}))
        )
      );
    }
  }

  &.--tags-variant-plain {
    --sp-comp-quantum-select-selected-tags-border-width: 0;
    --sp-comp-quantum-select-selected-tags-padding-inline: var(--sp-comp-text-field-plain-padding-inline, 0);
    --sp-comp-quantum-select-selected-tags-padding-block: var(--sp-comp-text-field-plain-padding-block, 0);
  }

  @include sp-breakpoint(xs) {
    --sp-comp-quantum-select-selected-tags-border-radius: var(
      --sp-comp-select-field-mobile-border-radius,
      var(--select-host-text-field-mobile-border-radius, var(--select-tags-default-border-radius))
    );
  }
}

.root.--is-active .sp-select.--selection-field {
  --sp-comp-quantum-select-selected-tags-border-color: var(--select-active-border-color);
  --sp-comp-quantum-select-selected-tags-focus-border-color: var(--select-active-border-color);
  --sp-comp-quantum-select-selected-tags-focus-outline: var(
    --sp-comp-select-active-outline,
    var(--select-tags-default-focus-outline)
  );
  --sp-comp-quantum-select-selected-tags-focus-box-shadow: var(
    --sp-comp-select-active-box-shadow,
    var(--select-tags-default-focus-box-shadow)
  );
}

.sp-select-activator__input.--selection-field {
  --sp-comp-text-field-min-width: 0;
  --sp-comp-text-field-min-height: auto;
  --sp-comp-text-field-compact-min-height: auto;
  --sp-comp-text-field-background-color: transparent;
  --sp-comp-text-field-focus-box-shadow: none;
  --sp-comp-text-field-focus-outline: none;
  --sp-comp-text-field-disabled-opacity: 1;
  box-shadow: none;
  border-radius: 0;
}

.sp-select-activator__input[inputmode="none"] {
  --sp-comp-text-field-cursor: pointer;
}

.sp-select-list-container {
  position: relative;
  width: 100%;
}

sp-label {
  margin-bottom: var(--sp-comp-select-label-margin-bottom, var(--sp-ref-scale-6));
  --sp-comp-text-label-short: var(--sp-comp-select-label-font, var(--sp-sys-form-field-label-short));
}

.root.--label-inline {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: var(--sp-comp-select-label-inline-gap, var(--sp-ref-scale-8));
  align-items: center;

  sp-label {
    margin-bottom: 0;
  }
}

sp-caret {
  --sp-comp-caret-size: var(--caret-size);
  /* The caret is box-centred, so it takes no nudge. */
  --sp-comp-caret-active-nudge: 0;
  color: var(--sp-comp-select-caret-color, currentColor);
  margin-inline-end: var(--sp-comp-select-caret-margin-inline-end, 0);
}

.sp-select__caret-image {
  display: block;
  width: var(--caret-size);
  height: var(--caret-size);
  margin-inline-end: var(--sp-comp-select-caret-margin-inline-end, 0);
  background-color: var(--sp-comp-select-caret-color, currentColor);
  /* A fully transparent mask, so nothing is drawn until an image is set. */
  mask: var(--sp-comp-select-caret-image, linear-gradient(transparent, transparent)) center / contain no-repeat;
  transform: translateY(var(--sp-comp-caret-offset, -0.125rem));
}

[slot="selection-trailing"],
.sp-select__trailing-append,
[slot="append"],
[slot="prepend"] {
  display: flex;
  align-items: center;
  justify-content: center;
}

[slot="append"],
.sp-select__trailing-append {
  padding-inline-end: var(--sp-comp-select-append-padding-inline-end, 0);
}

.sp-select-list__no-result {
  padding: var(--sp-comp-select-no-result-padding, 1rem);
  color: var(--sp-comp-select-no-result-color, #aaa);
}
</style>
